import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as NotificationsActions from '../notifications/notifications.actions';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { Store } from '@ngrx/store';
import { AppState } from '../app.reducer';
import { TranslateService } from '@ngx-translate/core';
import * as DeviceThActions from './device-th.actions';
import * as SiteActions from './../site/site.actions';
import { DeviceThService } from 'src/app/services/device-th.service';
import { WebsocketThermoService } from 'src/app/services/websocket-thermo.service';
import { UserService } from 'src/app/services/user.service';
import { selectCurrentDevice } from '../device/device.selector';

@Injectable()
export class DeviceThEffects {

    getDevicesThList$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceThActions.getDevicesThList),
        // withLatestFrom(
        //     this.store.select('site', 'currentSite'),
        // ),
        switchMap(() => this.deviceThService.getDevicesThList()
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceThActions.getDevicesThListSuccess({ thGroupsList: resp });
                    }
                    return DeviceThActions.getDevicesThListFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceThActions.getDevicesThListFailed({ errorMessage: error.message })))
            )
        )
    ));

    getDevicesThListV2$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceThActions.getDevicesThListV2),
        // withLatestFrom(
        //     this.store.select('site', 'currentSite'),
        // ),
        switchMap(() => this.deviceThService.getDevicesThListV2()
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceThActions.getDevicesThListV2Success({ thGroupsListV2: resp });
                    }
                    return DeviceThActions.getDevicesThListV2Failed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceThActions.getDevicesThListV2Failed({ errorMessage: error.message })))
            )
        )
    ));

    getDeviceThStatus$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceThActions.getDeviceThStatus),
        // withLatestFrom(
        //     this.store.select('site', 'currentSite'),
        // ),
        switchMap((action) => this.deviceThService.getDeviceThStatus(action.keycode)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceThActions.getDeviceThStatusSuccess({ thDeviceStatus: resp });
                    }
                    return DeviceThActions.getDeviceThStatusFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(DeviceThActions.getDevicesThListFailed({ errorMessage: error.message })))
            )
        )
    ));

    // GET EXPORT LOG
    getLogThExport$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceThActions.getLogThExport),
        withLatestFrom(
            this.store.select(selectCurrentDevice),
        ),
        switchMap(([action, device]) => this.deviceThService.getLogThExport(device.Keycode, action.startDate, action.endDate, action.metrics)
            .pipe(
                map(resp => {
                    if (resp.url != undefined) {
                        return DeviceThActions.getLogThExportSuccess({
                            logThExport: {
                                Url: resp.url,
                                FileType: resp.fileType
                            }
                        });
                    }
                    return DeviceThActions.getLogThExportFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceThActions.getLogThExportFailed({ errorMessage: error.message })))
            )
        )
    ));

    // TH LOG
    getThLog$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceThActions.getThLog),
        withLatestFrom(
            this.store.select(selectCurrentDevice),
        ),
        switchMap(([action, device]) => this.deviceThService.getThLog(device.Keycode, action.from, action.intervaltype, action.location)
            .pipe(
                map(resp => {
                    if (resp.Keycode !== undefined) {
                        return DeviceThActions.getThLogSuccess({
                            thermoLog: resp,
                        });
                    }
                    return DeviceThActions.getThLogFailed({ errorMessage: 'error' })
                }),
                catchError(() => of(DeviceThActions.getThLogFailed({ errorMessage: 'error' })))
            )
        )
    ));

    getThermoDevice$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceThActions.getThermoDevice),
        switchMap((action) => this.deviceThService.getThermoDevice(action.keycode).pipe(
            map((resp) => {
                if (resp !== undefined) {
                    return DeviceThActions.getThermoDeviceSuccess({ deviceThermo: resp });
                } else {
                    return DeviceThActions.getThermoDeviceFailed({ errorMessage: this.translate.instant('ERROR_GET_DATA_THERMO') });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(DeviceThActions.getThermoDeviceFailed({ errorMessage: this.translate.instant('ERROR_GET_DATA_THERMO') }));
            })
        ))
    ));

    updateThermo$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceThActions.updateThermo),
        switchMap((action) => this.deviceThService.updateThermo(action.thermo)
            .pipe(
                map(resp => {
                    if (resp.ok) {
                        return DeviceThActions.updateThermoSuccess({ message: this.translate.instant('SUCCESS_SET_DATA_THERMO') });
                    } else {
                        return DeviceThActions.updateThermoFailed({ errorMessage: this.translate.instant('ERROR_SET_DATA_THERMO') });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(DeviceThActions.updateThermoFailed({ errorMessage: this.translate.instant('ERROR_SET_DATA_THERMO') }));
                })
            ))
    ));

    openWebsocketThermo$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceThActions.openWebsocketThermo),
        withLatestFrom(
            this.store.select(selectCurrentDevice),
            this.store.select('auth', 'tokenData')
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        tap(([action, device, tokenData]) => {
            this.websocketThermoService.openWebSocket(tokenData.access_token, device.Keycode, device.Keycode);
        })
    ), { dispatch: false });

    closeWebsocketThermo$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceThActions.closeWebsocketThermo),
        tap(() => {
            this.websocketThermoService.closeWebSocket();
        })
    ), { dispatch: false });

    setGroupSiteAssociation$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceThActions.setGroupSiteAssociation),
        switchMap((action) => this.deviceThService.setGroupSiteAssociation(action.siteId, action.groupId).pipe(
            map((resp) => {
                if (resp !== undefined) {
                    return DeviceThActions.setGroupSiteAssociationSuccess({ message: this.translate.instant('TH_GROUP_ASSOCIATED_SUCCESS') });
                } else {
                    return DeviceThActions.setGroupSiteAssociationFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => {
                return of(DeviceThActions.setGroupSiteAssociationFailed({ errorMessage: error.ErrorMessage }));
            })
        ))
    ));

    refreshTH$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceThActions.setGroupSiteAssociationSuccess,
        ),
        tap(() => {
            this.store.dispatch(DeviceThActions.getDevicesThListV2());
            this.store.dispatch(SiteActions.getSites({}));

        })
    ), { dispatch: false });

    getFinalUsersThGroup$ = createEffect(() => this.actions$.pipe(
        // ofType(DeviceActions.getFinalUsers, DeviceActions.shareDeviceSuccess, InvitationsActions.invitationsShareDeviceSuccess),
        ofType(DeviceThActions.getFinalUsersThGroup),
        switchMap((action) => this.deviceThService.getFinalUsersThGroup(action.thGroupId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceThActions.getFinalUsersThGroupSuccess({ finalUsers: resp.Data });
                    } else {
                        return DeviceThActions.getFinalUsersThGroupFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceThActions.getFinalUsersThGroupFailed({ errorMessage: error.message })))
            )
        )
    ));

    removeShareThGroup$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceThActions.removeShareThGroup),
        switchMap((action) => {
            return this.deviceThService.removeShareThGroup(action.groupId, action.userId).pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceThActions.removeShareThGroupSuccess({ message: this.translate.instant('DELETE_FINALUSER_TH_GROUP_SUCCESS') });
                    } else {
                        return DeviceThActions.removeShareThGroupFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceThActions.removeShareThGroupFailed({ errorMessage: error.message })))
            )
        })
    ));

    revokeShareInvitation$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceThActions.revokeShareInvitationThGroup),
        switchMap((action) => {
            return this.deviceThService.revokeShareInvitationThGroup(action.token).pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceThActions.revokeShareInvitationThGroupSuccess({
                            message: this.translate.instant('REVOKE_INVITATION_SUCCESS'),
                        });
                    } else {
                        return DeviceThActions.revokeShareInvitationThGroupFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceThActions.revokeShareInvitationThGroupFailed({ errorMessage: error.message })))
            );
        })
    ));

    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceThActions.updateThermoSuccess,
            DeviceThActions.setGroupSiteAssociationSuccess,
            DeviceThActions.removeShareThGroupSuccess,
            DeviceThActions.revokeShareInvitationThGroupSuccess,
        ),
        tap((action) => {
            const notification = new Notification("DEVICE-THERMO", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    notifyError$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceThActions.getDevicesThListFailed,
            DeviceThActions.getDevicesThListV2Failed,
            DeviceThActions.getDeviceThStatusFailed,
            DeviceThActions.getLogThExportFailed,
            DeviceThActions.getThLogFailed,
            DeviceThActions.getThermoDeviceFailed,
            DeviceThActions.updateThermoFailed,
            DeviceThActions.setGroupSiteAssociationFailed,
            DeviceThActions.getFinalUsersThGroupFailed,
            DeviceThActions.removeShareThGroupFailed,
            DeviceThActions.revokeShareInvitationThGroupFailed,
        ),
        tap((action) => {
            if (action.errorMessage === '') {
                return;
            }
            const notification = new Notification("DEVICE-THERMO", action.errorMessage, NotificationLevel.Error, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private deviceThService: DeviceThService,
        private translate: TranslateService,
        private store: Store<AppState>,
        private websocketThermoService: WebsocketThermoService,
        private userService: UserService,
    ) { }
}

